import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useTheme } from '@mui/material';
import * as ACL from '@/domain/restriction';
import useOpenChat from '@/components/ContactSupportMessage/useOpenChat';
import useScript from '@/hooks/useScript';
import { getRuntimeConfig } from '@/lib/runtimeConfig';

import ListItem from '@mui/material/ListItem';
import { ListItemButton } from '@/pages/company/components/CompanyNavigation/StyledComponents';
import { ListItemIcon } from '@/components/mui/Layouts/components/Drawer';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@/components/mui/Tooltip';
import IconButton from '@mui/material/IconButton';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';

const ContactSupportLink = ({ expanded }) => {
  const { formatMessage } = useIntl();
  const openChat = useOpenChat();
  const isGranted = useSelector(ACL.isGranted);
  const { labelOptions } = useTheme();

  const isAllowedFreshdesk = isGranted(ACL.ALLOW_FRESHDESK);
  const isVisible = isAllowedFreshdesk || labelOptions.name === 'AlPrime';

  // useScript deduplicates: if HelpDropdown already added the script tag,
  // this hook reads the existing data-status attribute instead of loading again
  const freshdeskStatus = useScript(
    isAllowedFreshdesk
      ? `https://widget.freshworks.com/widgets/${getRuntimeConfig().env.REACT_APP_FRESHDESK_WIDGET_ID}.js`
      : undefined,
  );

  const isDisabled = isAllowedFreshdesk && freshdeskStatus !== 'ready';

  const handleClick = useCallback(() => {
    openChat();
  }, [openChat]);

  if (!isVisible) return null;

  return (
    <ListItem disablePadding sx={{ justifyContent: expanded ? 'start' : 'center' }}>
      {expanded ? (
        <ListItemButton onClick={handleClick} disabled={isDisabled}>
          <ListItemIcon
            sx={{
              color: 'inherit',
            }}
          >
            <LiveHelpIcon />
          </ListItemIcon>
          <ListItemText primary={formatMessage({ id: 'header.menu.info.support', defaultMessage: 'Contact support' })} />
        </ListItemButton>
      ) : (
        <Tooltip t={{ id: 'header.menu.info.support', defaultMessage: 'Contact support' }} placement="right">
          <span>
            <IconButton color="inherit" onClick={handleClick} disabled={isDisabled}>
              <LiveHelpIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </ListItem>
  );
};

export default ContactSupportLink;
